import { useMemo } from "react";
import { PSelect, Select } from "./Select/Select";

type PCurrenciesSelect<T extends object> = {
  listOfValues: T[];
  value: PSelect["value"];
  onChange: PSelect["onChange"];
  label: string;
  color?: string;
  baseColor?: string;
  borderColor?: string;
  expandedBorderColor?: string;
  titleColor?: string;
  currentId?: number;
  enableSearch?: boolean;
  searchableValues?: any;
  compact?: boolean;
  required?: boolean;
  placeholder?: string;
};

interface IBaseCurrency {
  id: any;
  currency: string;
  currencyLogo: string;
  isReceive?: boolean;
  isReceiveNetwork?: boolean;
  isToken?: boolean;
  currencyName?: string;
  networkName?: string;
  standard?: string;
  confirmations?: number;
  estimatedDurationInMins?: number;
}

export const CurrencyLogoDropdown = <T extends IBaseCurrency>(
  props: PCurrenciesSelect<T>
) => {
  const {
    listOfValues,
    value,
    onChange,
    label,
    color = "bg-page-body",
    baseColor = "bg-page-body",
    borderColor = "border-border-subtle",
    expandedBorderColor = "border-border-subtle",
    titleColor = "text-text-subtle",
    currentId,
    enableSearch = false,
    searchableValues,
    compact = false,
    required = false,
    placeholder = "Select",
  } = props;

  const options = useMemo(() => {
    return listOfValues.map((item) => ({
      label: compact ? (
        <div
          className={`flex flex-row justify-between max-w-[200px] w-full overflow-hidden ${
            item.isReceiveNetwork ? "mt-[5px]" : ""
          }`}
        >
          <div className="max-w-[100px] overflow-hidden flex-shrink">
            <div className="flex items-center gap-[8px] min-w-0">
              {item.currencyLogo ? (
                <img
                  src={item.currencyLogo}
                  className="max-h-[20px] flex-shrink-0"
                  alt=""
                />
              ) : null}
              <span className="text-text-base font-normal text-[12px] truncate">
                {item.currency}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-row justify-between w-full">
          <div>
            <div className="flex items-center gap-[10px]">
              {item.currencyLogo ? (
                <img src={item.currencyLogo} className="max-h-[20px]" alt="" />
              ) : null}
              <span className="text-text-base font-normal text-[12px]">
                {item.currency}
              </span>
            </div>
            {!item.isToken && item.isReceive && (
              <span
                className={`text-text-subtle text-[10px] ${
                  item.id === currentId ? "max-w-[120px] truncate block" : ""
                }`}
              >
                {item.networkName} ({item.standard})
              </span>
            )}
          </div>
        </div>
      ),
      selectedLabel: (
        <div className="flex items-center gap-[8px]">
          {item.currencyLogo ? (
            <img src={item.currencyLogo} className="max-h-[18px]" alt="" />
          ) : null}
          <span className="text-text-base text-[12px]">{item.currency}</span>
        </div>
      ),
      value: item.id,
    }));
  }, [listOfValues, compact, currentId]);

  return (
    <Select
      label={label}
      options={options}
      value={value}
      color={color}
      borderColor={borderColor}
      expandedBorderColor={expandedBorderColor}
      baseColor={baseColor}
      titleColor={titleColor}
      placeholder={placeholder}
      onChange={(val: any) => onChange && onChange(val)}
      getOptionCls={() => "text-text-base text-[12px]"}
      enableSearch={enableSearch}
      searchableLabels={searchableValues}
      required={required}
    />
  );
};
